<template>
    <div
        class="relative w-full h-full"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <img
            v-if="playbackId"
            :src="`https://image.mux.com/${playbackId}/thumbnail.jpg?time=${posterTime}`"
            :alt="alt"
            class="absolute inset-0 w-full h-full object-cover transition-opacity duration-200"
            :class="{ 'opacity-0': isHovering && playOnHover }"
        />
        
        <video
            ref="videoElement"
            :class="[videoClass, 'absolute inset-0 w-full h-full object-cover transition-opacity duration-200 pointer-events-none']"
            :style="{ opacity: isHovering && playOnHover ? 1 : 0 }"
            :muted="muted"
            :loop="loop"
            :playsinline="playsinline"
        ></video>

        <div
            v-if="showPlayOverlay && !isHovering"
            class="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center pointer-events-none"
        >
            <svg class="w-12 h-12 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
            </svg>
        </div>
    </div>
</template>

<script>
import Hls from 'hls.js';

export default {
    name: 'HlsVideoPlayer',
    props: {
        playbackId: {
            type: String,
            required: true
        },
        playOnHover: {
            type: Boolean,
            default: false
        },
        muted: {
            type: Boolean,
            default: true
        },
        loop: {
            type: Boolean,
            default: true
        },
        playsinline: {
            type: Boolean,
            default: true
        },
        autoplay: {
            type: Boolean,
            default: false
        },
        showPlayOverlay: {
            type: Boolean,
            default: true
        },
        posterTime: {
            type: Number,
            default: 0
        },
        alt: {
            type: String,
            default: ''
        },
        videoClass: {
            type: String,
            default: ''
        },
        initOnHover: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            hls: null,
            isHovering: false,
            isInitialized: false
        };
    },
    computed: {
        hlsUrl() {
            return `https://stream.mux.com/${this.playbackId}.m3u8`;
        }
    },
    mounted() {
        if (!this.initOnHover) {
            this.initializePlayer();
        }
    },
    beforeDestroy() {
        this.destroyPlayer();
    },
    watch: {
        playbackId() {
            this.destroyPlayer();
            this.isInitialized = false;
            if (!this.initOnHover) {
                this.initializePlayer();
            }
        }
    },
    methods: {
        onMouseEnter() {
            if (!this.playOnHover) return;

            this.isHovering = true;

            if (this.initOnHover && !this.isInitialized) {
                this.initializePlayer();
            }

            this.$nextTick(() => {
                this.play();
            });
        },
        onMouseLeave() {
            if (!this.playOnHover) return;

            this.isHovering = false;
            this.reset();

            if (this.hls && this.initOnHover) {
                this.hls.stopLoad();
            }
        },
        initializePlayer() {
            if (!this.playbackId || this.isInitialized) return;

            const video = this.$refs.videoElement;
            if (!video) return;

            if (Hls.isSupported()) {
                this.hls = new Hls({
                    enableWorker: true,
                    lowLatencyMode: false,
                    backBufferLength: 90
                });

                this.hls.loadSource(this.hlsUrl);
                this.hls.attachMedia(video);

                this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    this.isInitialized = true;
                    if (this.autoplay) {
                        video.play().catch(() => {
                            // Autoplay was prevented
                        });
                    }
                });

                this.hls.on(Hls.Events.ERROR, (event, data) => {
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                this.hls.startLoad();
                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                this.hls.recoverMediaError();
                                break;
                            default:
                                this.destroyPlayer();
                                break;
                        }
                    }
                });
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = this.hlsUrl;
                this.isInitialized = true;
                if (this.autoplay) {
                    video.play().catch(() => {
                        // Autoplay was prevented
                    });
                }
            }
        },
        destroyPlayer() {
            if (this.hls) {
                this.hls.destroy();
                this.hls = null;
            }
            this.isInitialized = false;
        },
        play() {
            const video = this.$refs.videoElement;
            if (video && this.isInitialized) {
                return video.play().catch(() => {
                    // Play was prevented
                });
            }
        },
        pause() {
            const video = this.$refs.videoElement;
            if (video) {
                video.pause();
            }
        },
        reset() {
            const video = this.$refs.videoElement;
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        }
    }
};
</script>
